/* eslint-disable no-unused-vars */
import React, {
    useState
} from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Button
} from 'shared/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    cn
} from 'shared/lib/utils';
import {
    Plus, Trash2, ArrowUp, ArrowDown, ChevronDown, ChevronRight,
    AlertCircle
} from 'lucide-react';
import {
    buildV2Step, changeStepKind
} from 'builders/builderOpcode/configMode/v2/V2StepBuilder';
import {
    StepCommonInlineFields, useProcedureStepIds
} from './_shared';
import dispatchInlineStep from './dispatchInlineStep';
import {
    LlmStepZone, LlmStepCanvas, llmFocusInputClass
} from './llmStepLayout';
import OpcodeStepKindPicker from '../OpcodeStepKindPicker';
import {
    STEP_KINDS, KIND_INDEX
} from '../stepKindCatalog';
import {
    formatStepKindOptionLabel
} from '../copy';

function kindMeta(kind) {
    return KIND_INDEX[kind] || KIND_INDEX.llm;
}

const PARALLEL_BRANCHES_HELP = (
    'Each branch is a full step (LLM, API, etc.) that runs at the same time. '
    + 'Give every branch a unique id — downstream steps read results as @branch_id.output '
    + '(e.g. @lookup_a.output.result). Add at least two branches; one branch is just a regular step. '
    + 'Use + Add branch to open the same step-kind picker as the main procedure. '
    + 'Nested Parallel branches are allowed. '
    + 'Control directives when, for, and repeat_until go on individual branches, not on this parallel step.'
);

/**
 * InlineParallelStep — kind:"parallel".
 * Runs `parallel.branches[]` concurrently. Each branch is itself any
 * step kind, so we recurse via dispatchInlineStep.
 *
 * Schema (V2StepParallel.tsx + SCHEMAS.md §4):
 *   { branches: [<JsonOpcodeStep>, …] }   (each branch has its own `id` + `kind`)
 *
 * Per A-0008.03 §2.2: `when` / `for` / `repeat_until` are NOT supported on
 * the parallel step itself — they live on individual branches. We don't
 * surface those legacy fields here; common fields (timeout/on_error/role)
 * are on the parallel step via the parent StepPane.
 *
 * Branch IDs are validated for uniqueness within the block.
 */
export default function InlineParallelStep({
    step, onPatchStep, definitions, flows, oelContext
}) {
    const branches = Array.isArray(step.branches) ? step.branches : [];
    const [expanded, setExpanded] = useState(branches.length > 0 ? 0 : null);
    const [showBranchKindPicker, setShowBranchKindPicker] = useState(false);
    const stepIdOptions = useProcedureStepIds(oelContext);

    const patch = (next) => onPatchStep({
        ...step,
        branches: next
    });

    function addBranch(kind) {
        const next = [...branches, buildV2Step(kind, branches)];
        patch(next);
        setExpanded(next.length - 1);
        setShowBranchKindPicker(false);
    }
    function removeBranch(i) {
        patch(branches.filter((_, j) => j !== i));
        if (expanded === i) setExpanded(null);
    }
    function moveBranch(i, dir) {
        const j = i + dir;
        if (j < 0 || j >= branches.length) return;
        const next = branches.slice();
        [next[i], next[j]] = [next[j], next[i]];
        patch(next);
        if (expanded === i) setExpanded(j);
        else if (expanded === j) setExpanded(i);
    }
    function patchBranch(i, updated) {
        patch(branches.map((b, j) => (j === i ? updated : b)));
    }
    function changeBranchKind(i, newKind) {
        patchBranch(i, changeStepKind(branches[i], newKind, branches));
    }

    // Detect duplicate ids inside this parallel block.
    const dupIds = (() => {
        const seen = {};
        const dups = new Set();
        for (const b of branches) {
            const id = b?.id;
            if (!id) continue;
            if (seen[id]) dups.add(id);
            seen[id] = true;
        }
        return dups;
    })();

    return (
        <LlmStepCanvas>
            <div
                data-testid="parallel-control-flow-note"
                className="flex items-start gap-2 rounded-xl border border-dashed border-fuchsia-200/80 bg-fuchsia-50/30 px-3 py-2 text-[11px] text-muted-foreground"
            >
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                    Control directives <code className="font-mono">when</code>, <code className="font-mono">for</code>,
                    and <code className="font-mono">repeat_until</code> apply to individual branches, not to the parallel
                    step itself (OPCODE-SCHEMA §6.3).
                </span>
            </div>

            <LlmStepZone
                variant="branches"
                title="Branches"
                description={
                    branches.length === 0
                        ? 'Add concurrent branches — each is a full step with its own id and config.'
                        : `${branches.length} branch${branches.length === 1 ? '' : 'es'} run concurrently — reference @branch_id.output in later steps.`
                }
                help={PARALLEL_BRANCHES_HELP}
            >
                {branches.length === 0 && (
                    <div className="rounded-md border border-dashed bg-white/60 px-3 py-2 text-[11.5px] text-muted-foreground">
                        No branches yet. Add at least two — a one-branch parallel is just a regular step.
                    </div>
                )}
                {branches.map((b, i) => {
                    const meta = kindMeta(b.kind);
                    const Icon = meta.icon;
                    const isOpen = expanded === i;
                    const dup = dupIds.has(b.id);
                    return (
                        <div
                            key={i}
                            className={cn(
                                'rounded-md border bg-card',
                                isOpen ? 'border-primary/40 shadow-sm' : 'border-border'
                            )}
                        >
                            {/* Branch header row */}
                            <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
                                <button
                                    type="button"
                                    onClick={() => setExpanded(isOpen ? null : i)}
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                </button>
                                <span className={cn('inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-muted', meta.color)}>
                                    <Icon className="h-3.5 w-3.5" />
                                </span>
                                <Input
                                    value={b.id || ''}
                                    onChange={(e) => patchBranch(i, {
                                        ...b,
                                        id: e.target.value
                                    })}
                                    placeholder="branch_id"
                                    className={cn(
                                        'h-7 font-mono text-[12.5px] flex-1 min-w-[120px] max-w-[200px]',
                                        llmFocusInputClass,
                                        dup && 'border-destructive text-destructive'
                                    )}
                                />
                                <Select value={b.kind || 'llm'} onValueChange={(k) => changeBranchKind(i, k)}>
                                    <SelectTrigger className={cn('h-7 min-w-[180px] max-w-[240px] text-[11.5px]', llmFocusInputClass)}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STEP_KINDS.map((k) => (
                                            <SelectItem key={k.kind} value={k.kind}>
                                                {formatStepKindOptionLabel(k.kind)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <div className="ml-auto flex items-center gap-0.5">
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); moveBranch(i, -1); }}
                                        disabled={i === 0}
                                        className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                                    >
                                        <ArrowUp className="h-3 w-3" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); moveBranch(i, 1); }}
                                        disabled={i === branches.length - 1}
                                        className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                                    >
                                        <ArrowDown className="h-3 w-3" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => removeBranch(i)}
                                        className="p-1 text-muted-foreground hover:text-destructive"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                </div>
                            </div>

                            {dup && (
                                <div className="flex items-start gap-1.5 border-b border-destructive/40 bg-destructive/5 px-3 py-1 text-[10.5px] text-destructive">
                                    <AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                                    <span>Duplicate branch id "<span className="font-mono">{b.id}</span>" — must be unique within this parallel block.</span>
                                </div>
                            )}

                            {/* Branch body — recursively dispatch */}
                            {isOpen && (
                                <div className="p-3">
                                    {dispatchInlineStep({
                                        step: b,
                                        onPatchStep: (updated) => patchBranch(i, updated),
                                        definitions,
                                        flows,
                                        oelContext
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}

                <div className="pt-1">
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className={cn(
                            'border border-primary/35 bg-primary/15 font-semibold text-primary shadow-none hover:bg-primary/25',
                            'h-8 text-xs'
                        )}
                        onClick={() => setShowBranchKindPicker(true)}
                    >
                        <Plus className="mr-1 h-3 w-3" /> Add branch
                    </Button>
                </div>
            </LlmStepZone>

            <OpcodeStepKindPicker
                open={showBranchKindPicker}
                onOpenChange={setShowBranchKindPicker}
                onSelect={addBranch}
                title="Add a branch"
                description={`${STEP_KINDS.length} step kinds — each branch runs concurrently. Pick what this branch should do.`}
            />

            <StepCommonInlineFields
                step={step}
                onPatchStep={onPatchStep}
                stepIdOptions={stepIdOptions}
            />
        </LlmStepCanvas>
    );
}
